/**
 * The trend between cycles — the rules over the run directory and the run digests
 * (bata-ui#166 W4-7, `docs/adr/9100`).
 *
 * The directory lists a cycle's runs one by one and publishes no run kind, so a trend over RUNS
 * would zig-zag: a price run proposes nothing and a replenishment run prices nothing. A cycle is
 * what the orchestrator ticked, and a run records its tick (`cycle_tick`, bata-platform ADR
 * 6034). So, amended at team review:
 *
 *   - a cycle is the runs sharing one tick INSTANT — `…Z` and `…+00:00` are one tick — and
 *     cycles are ordered by that instant, never by when the directory recorded them: a relaunch
 *     or backfill of an older tick is drawn where its tick is (ADR 6043);
 *   - a run with no tick, or one whose tick names no instant (no offset, or unreadable), is not a
 *     scheduled cycle: it is LEFT OUT of the series, and how many were left out is stated;
 *   - a run's kind is read from its digest — a replenishment run books proposals or transfers, a
 *     price run books price steps, and neither books the other's — because `entry_point_id` is
 *     one registry row both stages record. Within a tick only the NEWEST run (`recorded_at`) of
 *     each entry point and kind is counted: a re-execution under a new identity would otherwise
 *     double the units. The older ones are counted out and stated;
 *   - each series reads only the runs of the kind it measures. A tick with no replenishment run
 *     (a price-only tick, after the daily/weekly split, ADR 6048) is a GAP in units and fill
 *     rate, with that reason — never 0 — and likewise the price column for a tick with no price
 *     run;
 *   - a cycle any of whose digests could not be read is a GAP, never a partial sum and never 0;
 *   - the fill rate is Σ units / Σ requested over the cycle's replenishment lines — the digest's
 *     own definition over more lines — and absent when nothing was requested;
 *   - the directory is read one page deep; when that page is full, the tick of its last (oldest
 *     recorded) run may be missing runs that fell off the page, so it is dropped and the drop is
 *     stated.
 *
 * Pure, so `run-trend.test.ts` pins every rule on strings and numbers.
 */

import type { CycleRunSummary, InSeasonRunDigest } from '@/api/generated';
import type { ChartSpec, Datum } from '@/charts';

/** How many cycles the trend shows — explicit, and stated on the view. */
export const TREND_CYCLES = 6;

/**
 * How many runs the directory is asked for: four runs per tick at most today (replenishment,
 * push, transfer, price), times the cycles shown.
 */
export const TREND_DIRECTORY_LIMIT = TREND_CYCLES * 4;

export interface TrendCycle {
  /** Stable id: the tick instant. */
  readonly key: string;
  /** The orchestrator tick, verbatim, as the newest-recorded run of the cycle carries it. */
  readonly tick: string;
  /** The tick as an instant, epoch milliseconds — what cycles are grouped and ordered by. */
  readonly instant: number;
  /** The cycle's runs, newest recorded first, as the directory lists them. */
  readonly runs: readonly CycleRunSummary[];
}

export interface TrendWindow {
  /** Oldest tick first — the order the axis is drawn in. */
  readonly cycles: readonly TrendCycle[];
  /** Whether the tick of the page's last run was dropped because runs may have fallen off it. */
  readonly droppedPartial: boolean;
  /** Runs on the page left out because they carry no tick instant (manual, backfill). */
  readonly untagged: number;
}

/** A time of day followed by an offset: what makes an ISO 8601 stamp an instant. */
const WITH_OFFSET = /T\d{2}:\d{2}(?::\d{2}(?:\.\d+)?)?(?:z|[+-]\d{2}(?::?\d{2})?)$/i;

/**
 * A `cycle_tick` as an instant, or `null` when it is absent, carries no offset, or does not
 * parse — the reading bata-platform's cycle status gives it (ADR 6043), so a tick the platform
 * cannot order is not ordered here either.
 */
export function tickInstant(tick: string | null | undefined): number | null {
  if (typeof tick !== 'string') return null;
  const trimmed = tick.trim();
  if (trimmed === '' || !WITH_OFFSET.test(trimmed)) return null;
  const instant = Date.parse(trimmed);
  return Number.isNaN(instant) ? null : instant;
}

/** The directory page, grouped into cycles by tick instant, the newest `limit`, oldest first. */
export function trendCycles(
  runs: readonly CycleRunSummary[],
  { hasMore, limit = TREND_CYCLES }: { readonly hasMore: boolean; readonly limit?: number },
): TrendWindow {
  const groups = new Map<number, { tick: string; runs: CycleRunSummary[] }>();
  let untagged = 0;
  for (const run of runs) {
    const instant = tickInstant(run.cycle_tick);
    if (instant === null) {
      untagged += 1;
      continue;
    }
    const entry = groups.get(instant) ?? { tick: run.cycle_tick ?? '', runs: [] };
    entry.runs.push(run);
    groups.set(instant, entry);
  }
  // A full page may have cut short the tick of its last (oldest recorded) run; an untagged
  // run there is left out anyway.
  const lastRun = runs[runs.length - 1];
  const lastInstant = hasMore && lastRun !== undefined ? tickInstant(lastRun.cycle_tick) : null;
  if (lastInstant !== null) groups.delete(lastInstant);
  const ordered = [...groups.entries()]
    .sort(([a], [b]) => b - a)
    .map(([instant, entry]): TrendCycle => ({ key: `tick:${instant}`, tick: entry.tick, instant, runs: entry.runs }));
  return { cycles: ordered.slice(0, limit).reverse(), droppedPartial: lastInstant !== null, untagged };
}

/**
 * What a run is, read from its own book: `replenishment` when it proposes or transfers anything,
 * `price` when it prices anything, `empty` when its digest books nothing at all — which a price
 * run with candidates only can be, so an empty run is never taken as a replenishment run that
 * proposed zero.
 */
export type RunKind = 'replenishment' | 'price' | 'empty';

export function runKind(digest: InSeasonRunDigest): RunKind {
  const book = digest.proposals;
  if (book.proposals > 0 || book.requested_units > 0 || digest.transfers.transfers > 0) return 'replenishment';
  if (digest.price_steps.price_steps > 0) return 'price';
  return 'empty';
}

/** One cycle's headline figures. `null` where the cycle has no run of the figure's kind. */
export interface CycleFigures {
  /** Runs counted: the newest of each entry point and kind in the tick. */
  readonly runs: number;
  /** Older runs of the same entry point and kind in the tick, left out. */
  readonly reExecutions: number;
  /** Whether the tick has a replenishment run; when not, every replenishment figure is `null`. */
  readonly hasReplenishment: boolean;
  /** Whether the tick has a price run; when not, `priceSteps` is `null`. */
  readonly hasPrice: boolean;
  readonly proposals: number | null;
  readonly units: number | null;
  readonly requestedUnits: number | null;
  readonly unmetUnits: number | null;
  /** Σ units / Σ requested; `null` with no replenishment run or when its lines asked for nothing. */
  readonly fillRate: number | null;
  readonly priceSteps: number | null;
  readonly transfers: number | null;
  readonly transferUnits: number | null;
}

/**
 * What a cycle's digests say: its figures, `gap` when any of its runs' digests failed, and
 * `pending` while any is still being read. `digests` maps a run to its digest, `null` for a
 * failed read, absent for one still in flight.
 */
export type CycleOutcome =
  | { readonly kind: 'figures'; readonly figures: CycleFigures }
  | { readonly kind: 'gap'; readonly failedRuns: readonly string[] }
  | { readonly kind: 'pending' };

const newerRun = (a: CycleRunSummary, b: CycleRunSummary): boolean => {
  const at = Date.parse(a.recorded_at);
  const bt = Date.parse(b.recorded_at);
  if (at !== bt) return at > bt;
  return a.run_id > b.run_id;
};

export function cycleOutcome(
  cycle: TrendCycle,
  digests: ReadonlyMap<string, InSeasonRunDigest | null>,
): CycleOutcome {
  const failed = cycle.runs.filter((run) => digests.get(run.run_id) === null).map((run) => run.run_id);
  if (failed.length > 0) return { kind: 'gap', failedRuns: failed };
  if (cycle.runs.some((run) => !digests.has(run.run_id))) return { kind: 'pending' };
  // The newest run of each entry point and kind: a re-execution in the same tick is not a
  // second cycle's worth of units.
  const newest = new Map<string, { run: CycleRunSummary; digest: InSeasonRunDigest; kind: RunKind }>();
  for (const run of cycle.runs) {
    const digest = digests.get(run.run_id);
    if (digest === undefined || digest === null) continue;
    const kind = runKind(digest);
    const key = `${run.entry_point_id}|${kind}`;
    const held = newest.get(key);
    if (held === undefined || newerRun(run, held.run)) newest.set(key, { run, digest, kind });
  }
  const counted = [...newest.values()];
  const replenishment = counted.filter((entry) => entry.kind === 'replenishment');
  const price = counted.filter((entry) => entry.kind === 'price');
  const sum = (entries: typeof counted, pick: (digest: InSeasonRunDigest) => number): number =>
    entries.reduce((total, entry) => total + pick(entry.digest), 0);
  const hasReplenishment = replenishment.length > 0;
  const hasPrice = price.length > 0;
  const units = sum(replenishment, (d) => d.proposals.units);
  const requested = sum(replenishment, (d) => d.proposals.requested_units);
  const whenReplenished = (value: number): number | null => (hasReplenishment ? value : null);
  return {
    kind: 'figures',
    figures: {
      runs: counted.length,
      reExecutions: cycle.runs.length - counted.length,
      hasReplenishment,
      hasPrice,
      proposals: whenReplenished(sum(replenishment, (d) => d.proposals.served_lines ?? d.proposals.proposals)),
      units: whenReplenished(units),
      requestedUnits: whenReplenished(requested),
      unmetUnits: whenReplenished(sum(replenishment, (d) => d.proposals.unmet_units)),
      fillRate: hasReplenishment && requested > 0 ? units / requested : null,
      priceSteps: hasPrice ? sum(price, (d) => d.price_steps.price_steps) : null,
      transfers: whenReplenished(sum(replenishment, (d) => d.transfers.transfers)),
      transferUnits: whenReplenished(sum(replenishment, (d) => d.transfers.units)),
    },
  };
}

/** A gap on the chart: the cycle's digest was not read, which is not a cycle that did nothing. */
const GAP: Datum = { missing: 'not-reported' };

const datum = (outcome: CycleOutcome, pick: (figures: CycleFigures) => number | null): Datum => {
  if (outcome.kind !== 'figures') return GAP;
  const value = pick(outcome.figures);
  return value === null ? GAP : value;
};

/**
 * Units proposed and units left unmet, cycle by cycle — a line over a run-indexed ordinal axis
 * whose order is declared (oldest first). Both series in one unit.
 */
export function unitsTrendSpec(labels: readonly string[], outcomes: readonly CycleOutcome[]): ChartSpec {
  return {
    form: 'line',
    showMarkers: true,
    actionTitleKey: 'overview.trend.unitsTitle',
    actionTitleValues: { cycles: labels.length },
    perimeterKey: 'overview.trend.perimeter',
    valueAxis: { unitKey: 'inseason.unit.units', zeroBased: true },
    categoryAxis: { kind: 'ordinal', labelKey: 'overview.trend.cycleAxis', order: labels },
    series: [
      { id: 'units', nameKey: 'overview.trend.unitsSeries', data: outcomes.map((o) => datum(o, (f) => f.units)), distinguisher: 'marker-circle', slot: 1 },
      { id: 'unmet', nameKey: 'overview.trend.unmetSeries', data: outcomes.map((o) => datum(o, (f) => f.unmetUnits)), distinguisher: 'marker-square', slot: 2 },
    ],
  };
}

/** The fill rate against request, cycle by cycle, in percent. */
export function fillTrendSpec(labels: readonly string[], outcomes: readonly CycleOutcome[]): ChartSpec {
  return {
    form: 'line',
    showMarkers: true,
    actionTitleKey: 'overview.trend.fillTitle',
    actionTitleValues: { cycles: labels.length },
    perimeterKey: 'overview.trend.perimeter',
    valueAxis: { unitKey: 'inseason.unit.percent', zeroBased: true },
    categoryAxis: { kind: 'ordinal', labelKey: 'overview.trend.cycleAxis', order: labels },
    series: [{
      id: 'fill', nameKey: 'overview.trend.fillSeries',
      data: outcomes.map((o) => datum(o, (f) => (f.fillRate === null ? null : f.fillRate * 100))),
      distinguisher: 'marker-circle', slot: 1,
    }],
  };
}
